import { Outlet } from "react-router";
import { useState, useEffect, useCallback, useRef } from "react";
import { Masthead, LoadingIndicator } from "@uhg-harmony/react";
import { useAuth } from "react-oidc-context";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { runtimeConfig } from "../config/runtimeConfig";
import { clientsActions } from "../redux/clients/clientsSlice";
import { selectTemplatesLoading } from "../redux/templates/templatesTreeSelectors";
import { useGetTemplates } from "../hooks/useGetTemplates";
import { setAccount, setAccountId } from "../redux/metadata/metadataSlice";
import { selectUserRole } from "../redux/user/userSelectors";
import "../styles/sections/masthead-bar.css";
import { getToken } from "../config/getToken";
import { ToastProviderWrapper } from "../features/notifications/masthead/ToastProviderWrapper";
import { NotificationModal } from "../features/notifications/modals/NotificationModal";
import { SupportUploadModal } from "../../support";
import { isToastEnabled } from "./appLayout/appLayoutHelpers";
import { useAccountClientSelection } from "./appLayout/useAccountClientSelection";
import { useAppNotifications } from "./appLayout/useAppNotifications";
import { useMastheadProps } from "./appLayout/useMastheadProps";

export default function AppLayout() {
  const auth = useAuth();
  const userProfile = auth.user?.profile;
  const usernameFromAuth =
    userProfile?.name ||
    userProfile?.preferred_username ||
    userProfile?.email ||
    "";
  const emailFromAuth = userProfile?.email || "";
  const env = runtimeConfig.environment;
  const [toastNotificationsEnabled] = useState(() =>
    isToastEnabled(runtimeConfig.toastNotificationsEnabled),
  );

  const dispatch = useAppDispatch();
  const clientsState = useAppSelector((state) => state.clients);
  const userRole = useAppSelector(selectUserRole);
  const isAuthSyncComplete = useAppSelector(
    (state) => state.user.isAuthSyncComplete,
  );
  const accountId = useAppSelector((state) => state.metadata.accountId);
  const clientId = useAppSelector((state) => state.metadata.clientId);
  const metadataAccount = useAppSelector((state) => state.metadata.account);
  const templatesLoading = useAppSelector(selectTemplatesLoading);
  const templatesHasFetched = useAppSelector(
    (state) => state.templates.hasFetched,
  );
  const [initialTemplatesFetched, setInitialTemplatesFetched] = useState(false);
  const [isSupportUploadModalOpen, setIsSupportUploadModalOpen] =
    useState(false);
  const [isImageMetadataModalOpen, setIsImageMetadataModalOpen] =
    useState(false);
  const [token, setToken] = useState("");
  const requestedClientsRef = useRef(false);

  useEffect(() => {
    if (!isAuthSyncComplete) {
      setToken("");
      return;
    }

    let cancelled = false;
    void getToken().then((currentToken) => {
      if (!cancelled) setToken(currentToken || "");
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthSyncComplete]);

  useEffect(() => {
    if (!isAuthSyncComplete) {
      requestedClientsRef.current = false;
    }
  }, [isAuthSyncComplete]);

  useEffect(() => {
    if (
      isAuthSyncComplete &&
      !clientsState.hasFetched &&
      !clientsState.loading &&
      !requestedClientsRef.current
    ) {
      requestedClientsRef.current = true;
      void getToken().then((token) => {
        if (!token) {
          requestedClientsRef.current = false;
          return;
        }
        dispatch(clientsActions.getClientsByPlatformRequested({ token }));
      });
    }
  }, [
    dispatch,
    isAuthSyncComplete,
    clientsState.hasFetched,
    clientsState.loading,
  ]);

  useGetTemplates({
    platformId: accountId,
    clientId,
    enabled: isAuthSyncComplete && clientsState.hasFetched,
  });

  useEffect(() => {
    if (
      isAuthSyncComplete &&
      clientsState.hasFetched &&
      !templatesLoading &&
      templatesHasFetched &&
      !initialTemplatesFetched
    ) {
      setInitialTemplatesFetched(true);
    }
  }, [
    isAuthSyncComplete,
    clientsState.hasFetched,
    templatesLoading,
    templatesHasFetched,
    initialTemplatesFetched,
  ]);

  const {
    accounts,
    clients,
    selectedAccount,
    selectedClients,
    handleAccountChange,
    handleClientChange,
    resetAccountContext,
  } = useAccountClientSelection({ clientsState, userRole });

  const notificationMenuValues = useAppNotifications({
    env,
    userRole,
    selectedAccountValue: selectedAccount?.value,
    token,
    isAuthSyncComplete,
    clientsHasFetched: clientsState.hasFetched,
    clientsLoading: clientsState.loading,
  });

  const openSupportUploadModal = useCallback(() => {
    setIsSupportUploadModalOpen(true);
  }, []);

  const openImageMetadataModal = useCallback(() => {
    setIsImageMetadataModalOpen(true);
  }, []);

  const mastheadProps = useMastheadProps({
    usernameFromAuth,
    emailFromAuth,
    auth,
    token,
    env,
    onSupportUploadClick: openSupportUploadModal,
    onGetImageMetadataClick: openImageMetadataModal,
    notificationMenuValues,
    accounts,
    selectedAccount,
    setSelectedAccount: handleAccountChange,
    clients,
    selectedClients,
    setSelectedClients: handleClientChange,
    userRole,
    clientsLoading: clientsState.loading,
    treeLoading: templatesLoading || !templatesHasFetched,
  });

  // Handle account selection and update Redux
  useEffect(() => {
    if (!selectedAccount?.value || selectedAccount.value === "") {
      if (!metadataAccount && !accountId) return;
      dispatch(setAccount({ account: "" }));
      dispatch(setAccountId({ accountId: "" }));
      return;
    }

    if (!clientsState.data) return;

    const matchingPlatform = clientsState.data.find(
      (client) => client.platform === selectedAccount.value,
    );
    if (!matchingPlatform) return;

    const platformIdAcctId = String(matchingPlatform.platform_id);
    if (
      metadataAccount === selectedAccount.value &&
      accountId === platformIdAcctId
    ) {
      return;
    }
    dispatch(setAccount({ account: selectedAccount.value }));
    dispatch(setAccountId({ accountId: platformIdAcctId }));
  }, [
    selectedAccount,
    clientsState.data,
    dispatch,
    metadataAccount,
    accountId,
  ]);

  const isLoadingClients = clientsState.loading && !clientsState.data;
  const isLoadingTemplates =
    isAuthSyncComplete && clientsState.hasFetched && !initialTemplatesFetched;
  const showLoading = isLoadingClients || isLoadingTemplates;

  return (
    <>
      <Masthead {...mastheadProps} />
      <ToastProviderWrapper enabled={toastNotificationsEnabled}>
        <div>
          {showLoading ? (
            <LoadingIndicator
              loading
              loadingText="Loading..."
              centerSpinner
              displayOverlay={false}
              accessibilityText="Loading application"
            />
          ) : (
            <Outlet context={{ resetAccountContext }} />
          )}
        </div>
      </ToastProviderWrapper>
      <SupportUploadModal
        title="Upload Image"
        open={isSupportUploadModalOpen}
        onClose={() => setIsSupportUploadModalOpen(false)}
        token={token}
      />
      <SupportUploadModal
        title="getImageMetaData"
        open={isImageMetadataModalOpen}
        onClose={() => setIsImageMetadataModalOpen(false)}
        token={token}
        mode="metadata"
      />
      <NotificationModal />
    </>
  );
}
